import { Injectable, inject, signal } from '@angular/core';
import { finalize, forkJoin, tap } from 'rxjs';

import { ChartDataApiService } from './chart-data-api.service';
import {
  DEFAULT_PAGINATION_STATE,
  TablePaginationState,
} from '../../core/model/pagination.model';
import {
  BookingChartTableRowDTO,
  BookingStatsDTO,
  ChartSearchParams,
  GroupedBarChartData,
  PieChartData,
} from '../model/chart.model';

@Injectable({
  providedIn: 'root',
})
export class ChartDataService {
  readonly #chartApiService = inject(ChartDataApiService);
  readonly #chartTableRows = signal<BookingChartTableRowDTO[]>([]);
  readonly #countPerMonthRT = signal<GroupedBarChartData[]>([]);
  readonly #averagePerWeekday = signal<GroupedBarChartData[]>([]);
  readonly #averagePeoplePerRoomType = signal<GroupedBarChartData[]>([]);
  readonly #countPerStatus = signal<PieChartData[]>([]);
  readonly #activeFilters = signal<ChartSearchParams>({});
  readonly #isDataLoading = signal<boolean>(false);
  readonly #areStatsLoading = signal<boolean>(false);
  readonly #stats = signal<BookingStatsDTO | null>(null);
  readonly #tablePaginationState = signal<
    Pick<TablePaginationState, 'length' | 'pageIndex' | 'pageSize'>
  >({
    length: DEFAULT_PAGINATION_STATE.length,
    pageIndex: DEFAULT_PAGINATION_STATE.pageIndex,
    pageSize: 10,
  });

  get chartTableRows() {
    return this.#chartTableRows.asReadonly();
  }
  get countPerMonthRT() {
    return this.#countPerMonthRT.asReadonly();
  }
  get averagePerWeekday() {
    return this.#averagePerWeekday.asReadonly();
  }
  get averagePeoplePerRoomType() {
    return this.#averagePeoplePerRoomType.asReadonly();
  }
  get countPerStatus() {
    return this.#countPerStatus.asReadonly();
  }
  get isDataLoading() {
    return this.#isDataLoading.asReadonly();
  }

  get tablePaginationState() {
    return this.#tablePaginationState.asReadonly();
  }

  get areStatsLoading() {
    return this.#areStatsLoading.asReadonly();
  }

  get stats() {
    return this.#stats.asReadonly();
  }

  public loadChartData(queryParams?: ChartSearchParams) {
    if (queryParams) {
      this.#activeFilters.set(queryParams);
    }
    this.#tablePaginationState.update(state => ({ ...state, pageIndex: 0 }));

    this.#isDataLoading.set(true);
    return forkJoin({
      calculated: this.#chartApiService.getCalculatedChartData(
        this.#activeFilters()
      ),
      rows: this.#chartApiService.getChartTableRows(
        this.#activeFilters(),
        0,
        this.#tablePaginationState().pageSize
      ),
    }).pipe(
      tap(({ calculated, rows }) => {
        this.#countPerMonthRT.set(calculated.countPerMonthRT);
        this.#averagePerWeekday.set(calculated.averagePerWeekday);
        this.#averagePeoplePerRoomType.set(calculated.averagePeoplePerRoomType);
        this.#countPerStatus.set(calculated.countPerStatus);
        this.#chartTableRows.set(rows.content ?? []);
        this.#tablePaginationState.update(state => ({
          ...state,
          length: rows.totalSize ?? 0,
        }));
      }),
      finalize(() => this.#isDataLoading.set(false))
    );
  }

  public loadChartTableRows(pageIndex: number, pageSize: number) {
    this.#isDataLoading.set(true);
    return this.#chartApiService
      .getChartTableRows(this.#activeFilters(), pageIndex, pageSize)
      .pipe(
        tap(rows => {
          this.#chartTableRows.set(rows.content ?? []);
          this.#tablePaginationState.set({
            length: rows.totalSize ?? 0,
            pageIndex,
            pageSize,
          });
        }),
        finalize(() => this.#isDataLoading.set(false))
      );
  }

  public exportChartDataCsv() {
    return this.#chartApiService.exportChartDataCsv(this.#activeFilters());
  }

  public loadBookingStats() {
    this.#areStatsLoading.set(true);
    return this.#chartApiService.getBookingsStats().pipe(
      tap(stats => this.#stats.set(stats)),
      finalize(() => this.#areStatsLoading.set(false))
    );
  }
}
